import { memo } from 'react';
import { X, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { ConnectionStatus, FinalizedSegment } from '@/hooks/useDeepgramTranscriber';
import ImmersiveRecordButton from './ImmersiveRecordButton';
import ImmersiveWaveform from './ImmersiveWaveform';
import ImmersiveTranscription from './ImmersiveTranscription';
import RecordOnboarding from '@/components/RecordOnboarding';

interface ImmersiveRecorderProps {
  // Hook state
  connectionStatus: ConnectionStatus;
  segments: FinalizedSegment[];
  interimText: string;
  streamingText: string;
  duration: number;
  isNearLimit: boolean;
  error: string | null;
  analyserRef: React.MutableRefObject<AnalyserNode | null>;
  maxDuration?: number;
  // Actions
  onStart: () => void;
  onStop: () => void;
  onPause: () => void;
  onResume: () => void;
  // Navigation
  onCancel: () => void;
  // テキストが既にある場合（追加録音からの遷移用）
  hasTranscript?: boolean;
}

const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
};

const ImmersiveRecorder = memo<ImmersiveRecorderProps>(
  ({
    connectionStatus,
    segments,
    interimText,
    streamingText,
    duration,
    isNearLimit,
    error,
    analyserRef,
    maxDuration = 600,
    onStart,
    onStop,
    onPause,
    onResume,
    onCancel,
    hasTranscript,
  }) => {
    const isActive = connectionStatus === 'recording' || connectionStatus === 'paused';
    const showDoneButton = isActive || hasTranscript;

    return (
      <div className="fixed inset-0 z-50 bg-black flex flex-col" style={{ paddingTop: 'env(safe-area-inset-top)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
        {/* ─── Error / Near-limit banner ─── */}
        {error && (
          <div className="mx-4 mt-3 p-3 bg-red-500/20 border border-red-500/30 text-red-300 rounded-lg text-sm">
            {error}
          </div>
        )}
        {!error && isNearLimit && maxDuration > 0 && (
          <div className="mx-4 mt-3 flex justify-center">
            <span className="px-3 py-1 rounded-full bg-amber-500/20 text-amber-400 text-xs font-medium animate-pulse">
              残り{maxDuration - duration}秒
            </span>
          </div>
        )}

        {/* ─── Transcription area (flex-1, top ~60%) ─── */}
        <div className="flex-1 flex flex-col min-h-0 pt-4">
          <ImmersiveTranscription
            segments={segments}
            interimText={interimText}
            streamingText={streamingText}
          />
        </div>

        {/* ─── Waveform (compact) ─── */}
        <div className="w-full h-16 sm:h-20 px-6">
          <ImmersiveWaveform
            analyserRef={analyserRef}
            connectionStatus={connectionStatus}
          />
        </div>

        {/* ─── Record button ─── */}
        <div className="flex items-center justify-center py-4">
          <ImmersiveRecordButton
            connectionStatus={connectionStatus}
            onStart={onStart}
            onPause={onPause}
            onResume={onResume}
          />
        </div>

        {/* ─── Bottom toolbar: Cancel | Timer | Done ─── */}
        <div className="flex items-center justify-between px-8 pb-4">
          {/* Cancel (red X) */}
          <button
            onClick={onCancel}
            className="w-14 h-14 flex items-center justify-center rounded-xl bg-red-500/15 text-red-400 hover:bg-red-500/25 transition-colors"
            aria-label="キャンセル"
          >
            <X className="w-6 h-6" />
          </button>

          {/* Timer */}
          <span
            className={cn(
              'font-mono text-2xl tracking-wider',
              isNearLimit ? 'text-amber-400' : 'text-white/70'
            )}
          >
            {formatTime(duration)}
          </span>

          {/* Done (theme check) — only when active or has transcript */}
          {showDoneButton ? (
            <button
              onClick={onStop}
              className="w-14 h-14 flex items-center justify-center rounded-xl bg-primary/15 text-primary hover:bg-primary/25 transition-colors"
              aria-label="完了"
            >
              <Check className="w-6 h-6" />
            </button>
          ) : (
            <div className="w-14 h-14" />
          )}
        </div>

        {/* Onboarding hints for new users */}
        <RecordOnboarding />
      </div>
    );
  }
);
ImmersiveRecorder.displayName = 'ImmersiveRecorder';

export default ImmersiveRecorder;
